import type { DealBoardItem, SessionUser } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { formatCents, formatDate } from '../lib/format';

const ORDER = ['matched', 'intro_made', 'in_progress', 'closed_won', 'closed_lost', 'fell_through'] as const;

function statusPill(status: string) {
  const good = status === 'closed_won';
  const bad = status === 'closed_lost' || status === 'fell_through';
  return <span className={`pill ${good ? 'conf-high' : bad ? 'conf-low' : 'conf-medium'}`}>{status}</span>;
}

/** The deal-status board — every matched/closed deal, grouped by pipeline status. */
export function DealsBoardPage({ user }: { user: SessionUser }) {
  const deals = useQuery({ queryKey: ['deals'], queryFn: () => api.deals.list() });

  const groups: Record<string, DealBoardItem[]> = {};
  for (const d of deals.data?.items ?? []) (groups[d.status] ??= []).push(d);

  return (
    <Layout user={user}>
      <div className="row-between">
        <h2>Deal tracker</h2>
        <span className="muted">{deals.data?.items.length ?? 0} deal(s)</span>
      </div>
      {deals.isLoading ? (
        <p className="muted">Loading…</p>
      ) : deals.isError ? (
        <p className="error">{(deals.error as Error).message}</p>
      ) : (deals.data?.items.length ?? 0) === 0 ? (
        <p className="muted">No deals yet. A deal is created when a seller accepts an offer.</p>
      ) : (
        ORDER.filter((s) => groups[s]?.length).map((status) => (
          <div className="card" key={status}>
            <h3>
              {statusPill(status)} <span className="muted">· {groups[status]!.length}</span>
            </h3>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Dealer</th>
                    <th>Tier</th>
                    <th>Agreed</th>
                    <th>Realized</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {groups[status]!.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <Link to={`/deals/${d.id}`}>{d.model_designation ?? d.public_ref ?? d.id.slice(0, 8)}</Link>
                      </td>
                      <td>{d.dealer_business_name}</td>
                      <td className="muted">{d.tier_context}</td>
                      <td>{formatCents(d.agreed_price_cents)}</td>
                      <td>{d.closed_price_cents != null ? <strong>{formatCents(d.closed_price_cents)}</strong> : <span className="muted">—</span>}</td>
                      <td className="muted">{formatDate(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </Layout>
  );
}
